import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import { ChevronLeft, ChevronRight, Mic, Square, Upload, X, AlertCircle } from "lucide-react";
import { useLocale } from "@/lib/locale";
import { validatePhoto, validateVoiceFile, VOICE_MIN_SECONDS } from "@/lib/uploadValidation";

const LANGS = [
    { code: "it", label: "Italiano" },
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "pt", label: "Português" },
    { code: "fr", label: "Français" },
];

const EMOTIONAL_TRAIT_CHIPS = [
    "calda", "ironica", "protettiva", "schiva", "testarda", "dolce",
    "rumorosa", "silenziosa", "severa", "buffa", "romantica", "sognatrice",
    "pratica", "malinconica", "solare", "paziente",
];

export default function CreatePresence() {
    const nav = useNavigate();
    const { t } = useLocale();
    const [step, setStep] = useState(0);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const [name, setName] = useState("");
    const [relationship, setRelationship] = useState("");
    const [addressStyle, setAddressStyle] = useState("");
    const [language, setLanguage] = useState("it");
    const [photo, setPhoto] = useState(null);
    const [audio, setAudio] = useState(null);
    const [traits, setTraits] = useState("");
    const [tone, setTone] = useState("");
    const [phrases, setPhrases] = useState("");
    const [emotionalTraits, setEmotionalTraits] = useState([]);
    const [anchorMemory, setAnchorMemory] = useState("");
    const [farewellStyle, setFarewellStyle] = useState("");
    const [memories, setMemories] = useState("");
    const [neverSaid, setNeverSaid] = useState("");
    // Ethical consent checkpoint — kept on IdentityStep so every presence is
    // created intentionally. `isAlive` null means "not declared yet" and the
    // user must pick before leaving step 0. `hasConsent` is only meaningful
    // when `isAlive === true`.
    const [isAlive, setIsAlive] = useState(null);
    const [hasConsent, setHasConsent] = useState(false);

    const steps = [
        { key: "identity", title: t("wiz.title_identity"), eyebrow: t("wiz.chapter1") },
        { key: "photo",    title: t("wiz.title_photo"),    eyebrow: t("wiz.chapter2") },
        { key: "voice",    title: t("wiz.title_voice"),    eyebrow: t("wiz.chapter3") },
        { key: "tone",     title: t("wiz.title_tone"),     eyebrow: t("wiz.chapter4") },
        { key: "anchors",  title: t("wiz.title_anchors"),  eyebrow: t("wiz.chapter5") },
        { key: "memories", title: t("wiz.title_memories"), eyebrow: t("wiz.chapter6") },
        { key: "review",   title: t("wiz.title_review"),   eyebrow: t("wiz.chapter7") },
    ];

    const canNext = () => {
        if (step === 0) {
            if (!name.trim() || !relationship.trim()) return false;
            // Must pick a consent posture before leaving step 0. If the user
            // marked this person as living, they also need to acknowledge
            // consent — no silent bypass.
            if (isAlive === null) return false;
            if (isAlive === true && !hasConsent) return false;
            return true;
        }
        return true;
    };
    const next = () => setStep((s) => Math.min(steps.length - 1, s + 1));
    const prev = () => setStep((s) => Math.max(0, s - 1));

    // PhotoStep has already validated the file before calling us. Our job
    // is just the base64 conversion — no size/format/dimension checks here.
    const onPhoto = async (f) => {
        if (!f) return;
        const buf = await f.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const b64 = btoa(binary);
        setPhoto({ base64: b64, mime: f.type || "image/jpeg", url: URL.createObjectURL(f) });
    };

    const toggleTrait = (tr) => {
        setEmotionalTraits((cur) => cur.includes(tr) ? cur.filter((x) => x !== tr) : [...cur, tr]);
    };

    const submit = async () => {
        setBusy(true);
        setErr("");
        try {
            const { data: created } = await api.post("/presences", {
                name: name.trim(),
                relationship: relationship.trim(),
                photo_base64: photo?.base64 || null,
                photo_mime: photo?.mime || null,
                personality_traits: traits,
                memories,
                typical_phrases: phrases,
                tone_description: tone,
                address_style: addressStyle,
                emotional_traits: emotionalTraits.join(", "),
                never_said: neverSaid,
                anchor_memory: anchorMemory,
                farewell_style: farewellStyle,
                language,
                is_alive: isAlive,
                has_consent: isAlive === true ? hasConsent : null,
            });
            if (audio?.blob) {
                const fd = new FormData();
                const ext = (audio.mime.split("/")[1] || "webm").split(";")[0];
                fd.append("file", audio.blob, `voice.${ext}`);
                await api.post(`/presences/${created.presence_id}/voice`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }
            nav(`/presence/${created.presence_id}/arrives`, { state: { presence: created } });
        } catch (e) {
            // Backend returns structured errors as
            // { detail: { code: "audio_too_large", message: "..." } }.
            // Translate known codes through the same wizard locale keys the
            // inline validators use so the copy stays consistent.
            const detail = e?.response?.data?.detail;
            let msg;
            if (detail && typeof detail === "object" && detail.code) {
                const key = detail.code
                    .replace(/^photo_/, "photo_err_")
                    .replace(/^audio_/, "voice_err_");
                const localized = t(`wiz.${key}`);
                msg = localized && !localized.startsWith("wiz.") ? localized : (detail.message || t("wiz.err_generic"));
            } else {
                msg = (typeof detail === "string" ? detail : null) || t("wiz.err_generic");
            }
            setErr(msg);
            setBusy(false);
        }
    };

    return (
        <div className="grain min-h-screen candle-halo">
            <Header minimal />
            <main className="max-w-3xl mx-auto px-6 md:px-12 pt-12 pb-24">
                {/* Stepper — numbered + always visible. Keeps the user anchored:
                    they know which chapter they're in and how many remain. */}
                <div className="mb-12" data-testid="wizard-progress">
                    <div className="flex items-center justify-between mb-3">
                        <p className="label-eyebrow text-ink-gold">
                            {String(step + 1).padStart(2, "0")} / {String(steps.length).padStart(2, "0")}
                        </p>
                        <p className="text-xs tracking-widest uppercase text-ink-textMid hidden sm:block">
                            {steps[step].eyebrow}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {steps.map((s, i) => (
                            <div
                                key={s.key}
                                className={`h-[2px] flex-1 transition-colors duration-500 ${
                                    i < step ? "bg-ink-gold" : i === step ? "bg-ink-goldHi" : "bg-ink-border"
                                }`}
                            />
                        ))}
                    </div>
                </div>

                <div className="animate-fade-in" key={step}>
                    <p className="label-eyebrow mb-4 sm:hidden" data-testid="wizard-eyebrow">{steps[step].eyebrow}</p>
                    <h1 className="h-serif text-4xl md:text-5xl mb-10">{steps[step].title}</h1>

                    {step === 0 && (
                        <div className="mb-10 border border-ink-gold/40 bg-ink-surface/40 rounded-sm p-5 md:p-6" data-testid="wizard-ai-disclaimer">
                            <p className="label-eyebrow text-ink-gold mb-2">{t("wiz.ai_disclaimer_title")}</p>
                            <p className="text-ink-textMid leading-relaxed">
                                {t("wiz.ai_disclaimer_body")}
                            </p>
                        </div>
                    )}

                    {step === 0 && (
                        <div className="space-y-10">
                            <div>
                                <label className="label-eyebrow" htmlFor="p-name">{t("wiz.name")}</label>
                                <input id="p-name" data-testid="wiz-name" className="input-line mt-2" value={name} onChange={(e) => setName(e.target.value)} />
                            </div>
                            <div>
                                <label className="label-eyebrow" htmlFor="p-rel">{t("wiz.relationship")}</label>
                                <input id="p-rel" data-testid="wiz-relationship" className="input-line mt-2" placeholder={t("wiz.relationship_ph")} value={relationship} onChange={(e) => setRelationship(e.target.value)} />
                            </div>
                            <div>
                                <label className="label-eyebrow" htmlFor="p-addr">{t("wiz.address")}</label>
                                <input id="p-addr" data-testid="wiz-address-style" className="input-line mt-2" placeholder={t("wiz.address_ph")} value={addressStyle} onChange={(e) => setAddressStyle(e.target.value)} />
                                <p className="text-sm text-ink-textMid italic mt-2">{t("wiz.address_hint")}</p>
                            </div>
                            <div>
                                <label className="label-eyebrow" htmlFor="p-lang">{t("wiz.language")}</label>
                                <select id="p-lang" data-testid="wiz-language" className="input-line mt-2 bg-ink-deep" value={language} onChange={(e) => setLanguage(e.target.value)}>
                                    {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                                </select>
                            </div>

                            {/* ── Consent checkpoint. Not a wall, a mindful
                                pause. Two soft options; once chosen we
                                reflect back an ethical microcopy so the
                                user understands how the app treats them. */}
                            <div className="pt-2" data-testid="wiz-consent-block">
                                <label className="label-eyebrow">{t("wiz.consent_title")}</label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                                    <button
                                        type="button"
                                        onClick={() => { setIsAlive(false); setHasConsent(false); }}
                                        aria-pressed={isAlive === false}
                                        className={`card-paper text-left transition-all duration-300 ${
                                            isAlive === false
                                                ? "!border-ink-gold ring-1 ring-ink-gold/40"
                                                : "hover:!border-ink-textLow"
                                        }`}
                                        data-testid="wiz-consent-departed"
                                    >
                                        <p className="font-serif text-lg text-ink-textHigh leading-snug">
                                            {t("wiz.consent_departed")}
                                        </p>
                                        <p className="text-sm text-ink-textMid italic mt-2">
                                            {t("wiz.consent_departed_hint")}
                                        </p>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsAlive(true)}
                                        aria-pressed={isAlive === true}
                                        className={`card-paper text-left transition-all duration-300 ${
                                            isAlive === true
                                                ? "!border-ink-gold ring-1 ring-ink-gold/40"
                                                : "hover:!border-ink-textLow"
                                        }`}
                                        data-testid="wiz-consent-living"
                                    >
                                        <p className="font-serif text-lg text-ink-textHigh leading-snug">
                                            {t("wiz.consent_living")}
                                        </p>
                                        <p className="text-sm text-ink-textMid italic mt-2">
                                            {t("wiz.consent_living_hint")}
                                        </p>
                                    </button>
                                </div>

                                {isAlive === false && (
                                    <p className="mt-5 text-sm text-ink-textMid font-serif italic leading-relaxed border-l-2 border-ink-gold pl-4" data-testid="wiz-consent-ack-departed">
                                        {t("wiz.consent_ack_departed")}
                                    </p>
                                )}
                                {isAlive === true && (
                                    <div className="mt-5 border-l-2 border-ink-gold pl-4" data-testid="wiz-consent-living-form">
                                        <p className="text-sm text-ink-textMid font-serif italic leading-relaxed mb-3">
                                            {t("wiz.consent_ack_living")}
                                        </p>
                                        <label className="flex items-start gap-3 cursor-pointer group">
                                            <input
                                                type="checkbox"
                                                checked={hasConsent}
                                                onChange={(e) => setHasConsent(e.target.checked)}
                                                className="mt-1 h-4 w-4 accent-ink-gold flex-shrink-0"
                                                data-testid="wiz-consent-check"
                                            />
                                            <span className="text-sm text-ink-textHigh group-hover:text-ink-goldHi transition-colors">
                                                {t("wiz.consent_check_label")}
                                            </span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 1 && <PhotoStep photo={photo} onPhoto={onPhoto} />}
                    {step === 2 && <VoiceStep audio={audio} setAudio={setAudio} />}

                    {step === 3 && (
                        <div className="space-y-10">
                            <div>
                                <label className="label-eyebrow" htmlFor="p-traits">{t("wiz.traits")}</label>
                                <textarea id="p-traits" data-testid="wiz-traits" rows={4} className="textarea-paper mt-2" placeholder={t("wiz.traits_ph")} value={traits} onChange={(e) => setTraits(e.target.value)} />
                            </div>

                            <div>
                                <p className="label-eyebrow mb-3">{t("wiz.emotional_chips")}</p>
                                <div className="flex flex-wrap gap-2" data-testid="wiz-emotional-traits">
                                    {EMOTIONAL_TRAIT_CHIPS.map((tr) => {
                                        const active = emotionalTraits.includes(tr);
                                        return (
                                            <button
                                                type="button"
                                                key={tr}
                                                onClick={() => toggleTrait(tr)}
                                                className={`px-3 py-1.5 rounded-sm text-sm border transition-colors duration-300 ${
                                                    active
                                                        ? "bg-ink-gold text-ink-deep border-ink-gold"
                                                        : "bg-transparent text-ink-textMid border-ink-border hover:border-ink-textLow hover:text-ink-textHigh"
                                                }`}
                                                data-testid={`trait-chip-${tr}`}
                                            >
                                                {t(`wiz.trait.${tr}`)}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-sm text-ink-textMid italic mt-3">{t("wiz.emotional_hint")}</p>
                            </div>

                            <div>
                                <label className="label-eyebrow" htmlFor="p-tone">{t("wiz.tone")}</label>
                                <textarea id="p-tone" data-testid="wiz-tone" rows={4} className="textarea-paper mt-2" placeholder={t("wiz.tone_ph")} value={tone} onChange={(e) => setTone(e.target.value)} />
                            </div>

                            <div>
                                <label className="label-eyebrow" htmlFor="p-phrases">{t("wiz.phrases")}</label>
                                <textarea id="p-phrases" data-testid="wiz-phrases" rows={3} className="textarea-paper mt-2" placeholder={t("wiz.phrases_ph")} value={phrases} onChange={(e) => setPhrases(e.target.value)} />
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-10">
                            <p className="text-ink-textMid max-w-lg italic font-serif text-lg">
                                {t("wiz.anchors_intro_a")} <span className="text-ink-gold">{t("wiz.anchors_intro_i")}</span>{t("wiz.anchors_intro_b")}
                            </p>
                            <div>
                                <label className="label-eyebrow" htmlFor="p-anchor">{t("wiz.anchor_label")}</label>
                                <textarea id="p-anchor" data-testid="wiz-anchor" rows={4} className="textarea-paper mt-2" placeholder={t("wiz.anchor_ph")} value={anchorMemory} onChange={(e) => setAnchorMemory(e.target.value)} />
                            </div>
                            <div>
                                <label className="label-eyebrow" htmlFor="p-farewell">{t("wiz.farewell")}</label>
                                <input id="p-farewell" data-testid="wiz-farewell" className="input-line mt-2" placeholder={t("wiz.farewell_ph")} value={farewellStyle} onChange={(e) => setFarewellStyle(e.target.value)} />
                                <p className="text-sm text-ink-textMid italic mt-2">{t("wiz.farewell_hint")}</p>
                            </div>
                            <div>
                                <label className="label-eyebrow" htmlFor="p-never">{t("wiz.never")}</label>
                                <textarea id="p-never" data-testid="wiz-never" rows={3} className="textarea-paper mt-2" placeholder={t("wiz.never_ph")} value={neverSaid} onChange={(e) => setNeverSaid(e.target.value)} />
                            </div>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="space-y-8">
                            <label className="label-eyebrow" htmlFor="p-mem">{t("wiz.memories_label")}</label>
                            <textarea id="p-mem" data-testid="wiz-memories" rows={10} className="textarea-paper mt-2" placeholder={t("wiz.memories_ph")} value={memories} onChange={(e) => setMemories(e.target.value)} />
                            <p className="text-sm text-ink-textMid italic">{t("wiz.memories_private")}</p>
                        </div>
                    )}

                    {step === 6 && (
                        <ReviewStep
                            name={name} relationship={relationship} addressStyle={addressStyle}
                            photo={photo} audio={audio}
                            traits={traits} tone={tone} phrases={phrases}
                            emotionalTraits={emotionalTraits}
                            anchorMemory={anchorMemory} farewellStyle={farewellStyle}
                            neverSaid={neverSaid} memories={memories}
                        />
                    )}

                    {err && <p className="mt-8 text-ink-danger font-serif italic" data-testid="wizard-error">{err}</p>}
                </div>

                <div className="mt-16 flex items-center justify-between border-t border-ink-border pt-8">
                    {step > 0 ? (
                        <button onClick={prev} className="btn-ghost flex items-center gap-2" data-testid="wizard-back"><ChevronLeft size={16} /> {t("wiz.back")}</button>
                    ) : <span />}
                    {step < steps.length - 1 ? (
                        <button onClick={next} disabled={!canNext()} className="btn-primary flex items-center gap-2" data-testid="wizard-next">
                            {t("wiz.next")} <ChevronRight size={16} />
                        </button>
                    ) : (
                        <button onClick={submit} disabled={busy} className="btn-primary" data-testid="wizard-submit">
                            {busy ? t("wiz.submitting") : t("wiz.submit")}
                        </button>
                    )}
                </div>
            </main>
        </div>
    );
}

function PhotoStep({ photo, onPhoto }) {
    const { t } = useLocale();
    const inputRef = useRef(null);
    // Local "soft error" state — rendered as an inline guided card, not an
    // alert. When set, we hide the picker so the user isn't tempted to click
    // it by mistake; they go through the retry button.
    const [errCode, setErrCode] = useState(null);
    const [busy, setBusy] = useState(false);

    const openPicker = () => {
        setErrCode(null);
        inputRef.current?.click();
    };

    const handleFile = async (f) => {
        if (!f) return;
        setBusy(true);
        try {
            const r = await validatePhoto(f);
            if (!r.ok) {
                setErrCode(r.code);
                return;
            }
            setErrCode(null);
            await onPhoto(f);
        } finally {
            setBusy(false);
        }
    };

    const retry = () => {
        setErrCode(null);
        // Defer the click so React finishes clearing the error first —
        // otherwise some browsers bounce the file dialog on rapid re-open.
        setTimeout(() => inputRef.current?.click(), 50);
    };

    return (
        <div className="space-y-6">
            <p className="text-ink-textMid max-w-lg">{t("wiz.photo_body")}</p>

            {/* ── Happy path: picker / preview ───────────────────────── */}
            {!errCode && (
                <div
                    onClick={openPicker}
                    className="photo-frame aspect-[3/4] max-w-sm mx-auto cursor-pointer group hover:border-ink-goldLo transition-colors duration-500"
                    data-testid="wiz-photo-drop"
                >
                    {photo ? (
                        <img src={photo.url} alt="" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-700" />
                    ) : (
                        <div className="w-full h-full bg-ink-surface flex flex-col items-center justify-center gap-3 text-ink-textLow">
                            <Upload size={28} />
                            <p className="font-serif italic text-lg">{busy ? "…" : t("wiz.photo_pick")}</p>
                            <p className="text-xs">{t("wiz.photo_constraints")}</p>
                        </div>
                    )}
                </div>
            )}

            {/* ── Guided error card — no "Error" vocabulary. We name what
                went wrong, explain why it matters, and offer a retry. ─ */}
            {errCode && (
                <div
                    className="max-w-sm mx-auto card-paper text-left animate-fade-in"
                    data-testid="wiz-photo-error"
                    data-error-code={errCode}
                    role="alert"
                >
                    <div className="flex items-start gap-3 mb-3">
                        <AlertCircle size={18} className="text-ink-gold mt-0.5 flex-shrink-0" />
                        <p className="font-serif text-lg text-ink-textHigh leading-snug">
                            {t("wiz.photo_err_title")}
                        </p>
                    </div>
                    <p className="text-sm md:text-[0.9rem] text-ink-textMid leading-relaxed ml-7 mb-5">
                        {t(`wiz.photo_err_${errCode}`)}
                    </p>
                    <button
                        type="button"
                        onClick={retry}
                        className="btn-primary w-full"
                        data-testid="wiz-photo-retry"
                    >
                        {t("wiz.photo_err_retry")}
                    </button>
                </div>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="wiz-photo-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <p className="text-center text-ink-textLow text-xs italic">{t("wiz.photo_skip")}</p>
        </div>
    );
}

function VoiceStep({ audio, setAudio }) {
    const { t } = useLocale();
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [errCode, setErrCode] = useState(null);
    const [busy, setBusy] = useState(false);
    const mediaRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const fileRef = useRef(null);

    const start = async () => {
        setErrCode(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
                // Recorded audio: only the minimum-duration check applies
                // (format is always webm/mp4 from the browser, size is well
                // under cap). We reuse the same validator so the UX is the
                // same whether the user records or uploads.
                validateVoiceFile(new File([blob], `voice.webm`, { type: blob.type })).then((r) => {
                    if (!r.ok) {
                        setErrCode(r.code);
                        return;
                    }
                    setAudio({ blob, mime: blob.type, url: URL.createObjectURL(blob) });
                });
                stream.getTracks().forEach((track) => track.stop());
            };
            mediaRef.current = mr;
            mr.start();
            setRecording(true);
            setElapsed(0);
            timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
        } catch {
            setErrCode("no_mic");
        }
    };
    const stop = () => {
        mediaRef.current?.stop();
        clearInterval(timerRef.current);
        setRecording(false);
    };

    const onFile = async (f) => {
        if (!f) return;
        setBusy(true);
        try {
            const r = await validateVoiceFile(f);
            if (!r.ok) {
                setErrCode(r.code);
                return;
            }
            setErrCode(null);
            setAudio({ blob: f, mime: f.type || "audio/mpeg", url: URL.createObjectURL(f) });
        } finally {
            setBusy(false);
        }
    };

    const remove = () => setAudio(null);

    const retryUpload = () => {
        setErrCode(null);
        setTimeout(() => fileRef.current?.click(), 50);
    };

    return (
        <div className="space-y-10">
            <p className="text-ink-textMid max-w-lg">{t("wiz.voice_body")}</p>
            <div className="grid sm:grid-cols-2 gap-6">
                <div className="card-paper text-center">
                    <p className="label-eyebrow mb-4">{t("wiz.voice_record")}</p>
                    {!recording ? (
                        <button onClick={start} data-testid="wiz-record-start" className="btn-primary inline-flex items-center gap-2">
                            <Mic size={16} /> {t("wiz.voice_start")}
                        </button>
                    ) : (
                        <button onClick={stop} data-testid="wiz-record-stop" className="btn-danger inline-flex items-center gap-2">
                            <Square size={16} /> {t("wiz.voice_stop")} · {elapsed}s
                        </button>
                    )}
                </div>
                <div className="card-paper text-center">
                    <p className="label-eyebrow mb-4">{t("wiz.voice_upload")}</p>
                    <button
                        onClick={() => { setErrCode(null); fileRef.current?.click(); }}
                        data-testid="wiz-upload-audio-btn"
                        disabled={busy}
                        className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        <Upload size={16} /> {busy ? "…" : t("wiz.voice_choose")}
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="audio/mpeg,audio/mp3,audio/wav,audio/wave,audio/x-wav,audio/mp4,audio/x-m4a,audio/m4a,audio/webm,audio/ogg,audio/oga"
                        className="hidden"
                        data-testid="wiz-upload-audio-input"
                        onChange={(e) => onFile(e.target.files?.[0])}
                    />
                </div>
            </div>

            {/* ── Guided error card — same shape as PhotoStep for visual
                coherence across the wizard. Covers: too short / too large
                / unsupported format / corrupted file / microphone denied. */}
            {errCode && (
                <div
                    className="card-paper max-w-lg mx-auto animate-fade-in"
                    data-testid="wiz-voice-error"
                    data-error-code={errCode}
                    role="alert"
                >
                    <div className="flex items-start gap-3 mb-3">
                        <AlertCircle size={18} className="text-ink-gold mt-0.5 flex-shrink-0" />
                        <p className="font-serif text-lg text-ink-textHigh leading-snug">
                            {errCode === "no_mic" ? t("wiz.voice_no_mic") : t("wiz.voice_err_title")}
                        </p>
                    </div>
                    {errCode !== "no_mic" && (
                        <p className="text-sm md:text-[0.9rem] text-ink-textMid leading-relaxed ml-7 mb-5">
                            {t(`wiz.voice_err_${errCode}`)}
                        </p>
                    )}
                    {errCode !== "no_mic" && (
                        <button
                            type="button"
                            onClick={retryUpload}
                            className="btn-primary ml-7"
                            data-testid="wiz-voice-retry"
                        >
                            {t("wiz.voice_err_retry")}
                        </button>
                    )}
                </div>
            )}

            {audio && !errCode && (
                <div className="card-paper relative" data-testid="wiz-audio-preview">
                    <button onClick={remove} className="absolute top-4 right-4 btn-ghost" aria-label="remove"><X size={16} /></button>
                    <p className="label-eyebrow mb-3">{t("wiz.voice_preview")}</p>
                    <audio src={audio.url} controls className="w-full" />
                    <p className="text-sm text-ink-textMid italic mt-3">{t("wiz.voice_caption")}</p>
                </div>
            )}
            <p className="text-center text-ink-textLow text-xs italic">{t("wiz.voice_skip")}</p>
        </div>
    );
}

function ReviewStep(props) {
    const { t } = useLocale();
    const { name, relationship, addressStyle, photo, audio, traits, tone, phrases, emotionalTraits, anchorMemory, farewellStyle, neverSaid, memories } = props;
    const Row = ({ label, value }) => (
        <div className="border-t border-ink-border py-5">
            <p className="label-eyebrow">{label}</p>
            <p className="mt-2 text-ink-textHigh font-serif text-lg leading-snug whitespace-pre-wrap">
                {value ? value : <span className="italic text-ink-textLow">{t("wiz.review_none")}</span>}
            </p>
        </div>
    );
    return (
        <div className="grid md:grid-cols-[200px_1fr] gap-10 items-start">
            <div className="photo-frame aspect-[3/4]">
                {photo ? (
                    <img src={photo.url} alt="" className="w-full h-full object-cover grayscale" />
                ) : (
                    <div className="w-full h-full bg-ink-surface flex items-center justify-center text-ink-textLow font-serif italic text-3xl">
                        {name?.[0] || "·"}
                    </div>
                )}
            </div>
            <div>
                <h2 className="font-serif text-3xl text-ink-textHigh">{name || "—"}</h2>
                <p className="text-ink-textMid mt-1">{relationship || "—"}</p>
                <p className="label-eyebrow mt-2 text-ink-gold">{audio ? t("wiz.review_voice_on") : t("wiz.review_voice_off")}</p>
                <div className="mt-8">
                    <Row label={t("profile.f_address")} value={addressStyle} />
                    <Row label={t("profile.f_personality")} value={traits} />
                    <Row label={t("profile.f_emotional")} value={emotionalTraits?.map((tr) => t(`wiz.trait.${tr}`)).join(", ")} />
                    <Row label={t("profile.f_tone")} value={tone} />
                    <Row label={t("profile.f_phrases")} value={phrases} />
                    <Row label={t("profile.f_anchor")} value={anchorMemory} />
                    <Row label={t("profile.f_farewell")} value={farewellStyle} />
                    <Row label={t("profile.f_never")} value={neverSaid} />
                    <Row label={t("profile.f_memories")} value={memories} />
                </div>
            </div>
        </div>
    );
}
